import { type ElementType, useEffect, useRef, useState } from 'react';
import {
  Heading,
  type HeadingVariant,
  Paragraph,
  type ParagraphVariant,
} from '@hungryroot/ui-kit';

const HEADING_VARIANTS: Set<string> = new Set<HeadingVariant>([
  'Hero',
  'HeroAlt',
  'H1',
  'H1Alt',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'H7',
]);

export type AnimatedTextVariant = HeadingVariant | ParagraphVariant;

const DEFAULT_MS_PER_CHAR = 20;

interface Props {
  text: string;
  variant?: AnimatedTextVariant;
  /** Element tag override. Forwarded to Heading/Paragraph `as` prop. */
  as?: ElementType;
  /** Reveal cadence per character. Defaults to 20ms (matches AiLoadingScreen). */
  msPerChar?: number;
  /** Delay before the first character is revealed. */
  startDelayMs?: number;
  onComplete?: () => void;
  className?: string;
  testid?: string;
}

/**
 * Reveals `text` one character at a time. The reveal is monotonic: when `text`
 * grows (e.g. a streaming response), the component continues revealing from
 * where it left off. When `text` stops growing the component idles once
 * caught up. Callers that want to restart the reveal for a new string should
 * change the `key` prop.
 *
 * When the user prefers reduced motion, the full text is shown immediately.
 */
export function AnimatedText({
  text,
  variant = 'body',
  as,
  msPerChar = DEFAULT_MS_PER_CHAR,
  startDelayMs = 0,
  onComplete,
  className,
  testid,
}: Props) {
  const [visibleCount, setVisibleCount] = useState(0);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  // Refs so the scheduler can read the latest values without re-creating the
  // effect (which would reset the timer on every streaming delta).
  const textRef = useRef(text);
  textRef.current = text;
  const visibleCountRef = useRef(visibleCount);
  visibleCountRef.current = visibleCount;
  const wakeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setVisibleCount(textRef.current.length);
      if (textRef.current.length > 0) {
        onCompleteRef.current?.();
      }
      return;
    }

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    // Ensure `onComplete` fires at most once per distinct "caught up" length.
    let lastCompletedLength = -1;
    let hasStartedReveal = false;

    const tick = () => {
      timeoutId = null;
      if (cancelled) return;

      const length = textRef.current.length;
      const next = Math.min(visibleCountRef.current + 1, length);
      visibleCountRef.current = next;
      setVisibleCount(next);

      if (next === length && length > 0 && lastCompletedLength !== length) {
        lastCompletedLength = length;
        onCompleteRef.current?.();
      }

      // Only keep the timer alive while there's more to reveal. When caught
      // up we idle until `wake()` fires from the text-watcher effect below.
      if (next < length) {
        scheduleTick(msPerChar);
      }
    };

    const scheduleTick = (delay: number) => {
      if (cancelled || timeoutId !== null) return;
      timeoutId = setTimeout(tick, delay);
    };

    const wake = () => {
      if (cancelled) return;
      if (visibleCountRef.current >= textRef.current.length) return;
      const delay = hasStartedReveal
        ? msPerChar
        : msPerChar + Math.max(0, startDelayMs);
      hasStartedReveal = true;
      scheduleTick(delay);
    };

    wakeRef.current = wake;
    wake();

    return () => {
      cancelled = true;
      wakeRef.current = null;
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    };
  }, [msPerChar, startDelayMs]);

  // Restart the scheduler whenever `text` grows past what's been revealed.
  useEffect(() => {
    wakeRef.current?.();
  }, [text]);

  const revealed = text.slice(0, visibleCount);
  // Non-breaking space keeps layout height stable before the first char lands.
  const display = revealed.length === 0 ? '\u00A0' : revealed;

  if (HEADING_VARIANTS.has(variant)) {
    return (
      <Heading
        variant={variant as HeadingVariant}
        as={as ?? 'span'}
        testid={testid}
        className={className}
      >
        {display}
      </Heading>
    );
  }

  return (
    <Paragraph
      variant={variant as ParagraphVariant}
      as={as ?? 'span'}
      testid={testid}
      className={className}
    >
      {display}
    </Paragraph>
  );
}

function prefersReducedMotion(): boolean {
  if (
    typeof window === 'undefined' ||
    typeof window.matchMedia !== 'function'
  ) {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
